import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { Button } from 'primereact/button';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Dialog } from "primereact/dialog";
import { Tooltip } from 'primereact/tooltip';
import { ProgressBar } from 'primereact/progressbar';
import { format } from 'date-fns';

const GlobalTable = ({
    columns,
    data,
    tabletitle,
    headerAction,
    onRowEditComplete,
    onRowEditInit,
    customEditors = {},
    globalFilterEnabled = true,
    pagination = { rows: 15 },
    scrollable = true,
    scrollHeight = '600px',
    additionalActions = null,
    showEditColumn = true,
    userManager = false,
    showEditDeleteColumn = false,
    selectable = false,
    selection,
    onSelectionChange,
    handleResetPassword, 
    handleDeleteRecord,
    loading,
    allowEdit = true,
    lazy = false,
    totalRecords = 0,
    onPage = null,
    onSort = null,
    onFilter = null,
    lazyParams = null,
}) => {
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState(lazyParams?.filters || {});
    const tableRef = useRef(null);
    const searchDebounceRef = useRef(null);

    useEffect(() => {
        if (!lazy) initFilters();
    }, [data, columns, lazy]);

    // In lazy mode, filters are owned by the parent (lazyParams) — keep local state in
    // sync. lazyParams.filters only ever carries `global` (useLazyTable has no idea
    // what columns exist), so merge it onto a full per-column skeleton rather than
    // replacing the whole filters object — otherwise every column's filter menu has
    // no constraints to read, which is what caused the missing inputs / "Cannot read
    // properties of undefined (reading 'operator')" on Clear.
    useEffect(() => {
        if (lazy && lazyParams?.filters) {
            setFilters({ ...buildBlankFilters(), ...lazyParams.filters });
        }
    }, [lazy, lazyParams?.filters]);

    // Convert string values to Date objects for date-type columns
    const processedData = React.useMemo(() => {
        if (!data) return [];
        const dateCols = columns.filter(col => col.dataType === 'date').map(col => col.accessor);
        if (dateCols.length === 0) return data;
        return data.map(row => {
            const updated = { ...row };
            dateCols.forEach(field => {
                if (updated[field] && !(updated[field] instanceof Date)) {
                    const parsed = new Date(updated[field]);
                    updated[field] = isNaN(parsed) ? updated[field] : parsed;
                }
            });
            return updated;
        });
    }, [data, columns]);

    const dateFilterTemplate = (options) => (
        <Calendar
            value={options.value}
            onChange={(e) => options.filterCallback(e.value, options.index)}
            dateFormat="yy-mm-dd"
            placeholder="Pick a date"
            showButtonBar
        />
    );

    const ellipsisBodyTemplateWrapper = (rowData, col) => 
        ellipsisBodyTemplate(rowData, col, col.bodyTemplate);

    const buildBlankFilters = () => {
        const columnFilters = {};
        columns.forEach((col) => {
            columnFilters[col.accessor] = {
                operator: FilterOperator.AND,
                constraints: [{
                value: null,
                matchMode: col.dataType === 'date'
                    ? FilterMatchMode.DATE_IS      
                    : col.matchMode || FilterMatchMode.CONTAINS
            }],
            };
        });

        return {
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            ...columnFilters,
        };
    };

    const initFilters = () => setFilters(buildBlankFilters());

    const clearFilter = () => {
        const blank = buildBlankFilters();
        setFilters(blank);
        setGlobalFilterValue('');
        if (lazy && onFilter) onFilter({ filters: blank, first: 0 });
    };

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        setGlobalFilterValue(value);
        setFilters((prevFilters) => {
            const next = { ...prevFilters, global: { ...prevFilters.global, value } };
            if (lazy && onFilter) {
                if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
                searchDebounceRef.current = setTimeout(() => {
                    onFilter({ filters: next, first: 0 });
                }, 300);
            }
            return next;
        });
    };

    // Column "menu" filters (funnel icon) apply immediately, no debounce needed
    const handleLazyFilter = (e) => {
        setFilters(e.filters);
        if (lazy && onFilter) onFilter({ filters: e.filters, first: 0 });
    };

    const renderHeader = () => (
        <div className="flex justify-content-between align-items-center gap-4 md:gap-2">
            <h5 className="text-base sm:text-lg lg:text-xl">{tabletitle}</h5>
            {globalFilterEnabled && (
                <div className="flex flex-wrap align-items-center gap-4 sm:gap-2">
                    {headerAction}
                    <IconField iconPosition="left" className="w-full sm:w-auto">
                        <InputIcon className="pi pi-search" />
                        <InputText
                            value={globalFilterValue}
                            onChange={onGlobalFilterChange}
                            placeholder="Keyword Search..."
                            className="w-full sm:w-auto"
                        />
                    </IconField>
                    <Button type="button" icon="pi pi-filter-slash" label="Clear" outlined onClick={clearFilter}  className="w-full sm:w-auto"/>
                </div>
            )}
        </div>
    );

    const header = renderHeader();

    // Default Text Editor
    const textEditor = (options) => (
        <InputText type="text" value={options.value} onChange={(e) => options.editorCallback(e.target.value)} />
    );

    const getEditor = (options, accessor, isReadOnly, dataType, bodyTemplate) => {
        if (isReadOnly) {
            if (bodyTemplate) {
                return bodyTemplate(options.rowData); // Render the custom body template
            }
            
            // If no bodyTemplate is defined, fallback to displaying the value as a label
            return <label>{options.rowData[accessor] || "N/A"}</label>;
        }
        if (customEditors[accessor]) {
            return customEditors[accessor](options);
        }
        if (dataType==='numeric'){
            return (
                <InputNumber
                    value={options.value}
                    onValueChange={(e) => options.editorCallback(e.value)}
                />
            );
        }
        if (dataType==='date'){
            return (
                <Calendar
                    value={new Date(options.value)}
                    onChange={(e) => options.editorCallback(e.value)}
                />
            );
        }
        if (dataType==='currency'){
            return (
                <InputNumber
                    value={options.value}
                    onValueChange={(e) => options.editorCallback(e.value)}
                    mode="currency" 
                    currency="LKR"
                />
            );
        }
        return textEditor(options);
    };

    const [isDialogVisible, setDialogVisible] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [selectedRow, setSelectedRow] = useState(null);

    const openDialog = (rowData) => {
        setSelectedRow(rowData); // Store the row data for the reset action
        setDialogVisible(true); // Show the dialog
    };

    const handleSavePassword = () => {
        if (newPassword.trim() === "") {
            alert("Password cannot be empty!");
            return;
        }
        // Call the reset password handler with the new password and row data
        if (handleResetPassword && selectedRow) {
            handleResetPassword(selectedRow, newPassword);
        }
        setDialogVisible(false); // Close the dialog
        setNewPassword(""); // Clear the password input
    };

    const handleDelete = (rowData) =>{
        handleDeleteRecord(rowData);
    }

    const formatDateToLocalTime = (value) => {
        if (!value) return ''; 
        return format(new Date(value), 'Pp');
    };

    // Template to render date columns
    const dateBodyTemplate = (rowData, column) => {
        const value = rowData[column.accessor];
        return formatDateToLocalTime(value);
    };

    const ellipsisBodyTemplate = (rowData, column, bodyTemp) => {
        let rawValue = bodyTemp
            ? (typeof bodyTemp === "function" ? bodyTemp(rowData) : bodyTemp)
            : rowData[column.accessor];

        if (rawValue == null) {
            return '';
        }
        if (typeof rawValue !== 'string' && typeof rawValue !== 'number') {
            return rawValue;
        }

        const value = String(rawValue);
        const tooltipId = `${column.accessor}-${rowData.id}`;

        // Truncation is left entirely to CSS (overflow/text-overflow below) so it
        // always matches the cell's actual current width — including right after a
        // column resize — instead of a stale, JS-measured pixel width that only
        // updated on data changes or a window resize, never on the resize drag itself.
        return (
            <div
                className="ellipsis-cell"
                id={tooltipId}
                data-pr-tooltip={value}
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}
            >
                <Tooltip target={`#${tooltipId}`} />
                {value}
            </div>
        );
    };

    
    return (
        <div>
            <div ref={tableRef}>
            <DataTable
                value={data}
                paginator                 
                lazy={lazy}
                totalRecords={lazy ? totalRecords : data?.length}
                first={lazy && lazyParams ? lazyParams.first : 0}
                rows={lazy && lazyParams ? lazyParams.rows : pagination.rows}
                onPage={lazy ? onPage : undefined}
                sortField={lazy && lazyParams ? lazyParams.sortField : undefined}
                sortOrder={lazy && lazyParams ? lazyParams.sortOrder : undefined}
                onSort={lazy ? onSort : undefined}
                rowsPerPageOptions={[5, 10, 15, 30, 50]}
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                dataKey="id"
                header={header}
                emptyMessage="No Records Found."
                globalFilterFields={columns.map((col) => col.accessor)}
                filters={filters}
                filterDisplay="menu"
                onFilter={lazy ? handleLazyFilter : (e) => setFilters(e.filters)}
                resizableColumns
                columnResizeMode="expand"
                showGridlines
                editMode="row"
                onRowEditComplete={onRowEditComplete}
                onRowEditInit={onRowEditInit}
                scrollable={scrollable}
                scrollHeight={scrollHeight}
                selectionMode={selectable}
                selection={selection}
                onSelectionChange={onSelectionChange}
                loading={loading}
            >
                {columns.map((col, idx) => (
                    <Column
                        key={idx}
                        field={col.accessor}
                        header={col.Header}
                        filter={col.filtered}
                        filterField={col.accessor}
                        filterElement={col.dataType === 'date' ? dateFilterTemplate : col.filterElement}
                        sortable={col.sortable}
                        dataType= {col.dataType} 
                        body={col.dataType === 'date' ? (rowData) => dateBodyTemplate(rowData, col) : col.dataType === 'icon' ? col.bodyTemplate : (rowData) => ellipsisBodyTemplateWrapper(rowData, col)}
                        editor={(options) => getEditor(options, col.accessor, col.readOnly, col.dataType, col.bodyTemplate)}
                        style={col.maxWidth
                            ? { maxWidth: col.maxWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
                            : { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    />
                ))}
                {additionalActions && additionalActions.map((action, idx) => <Column key={idx} {...action} />)}
                {showEditColumn && (
                    <Column
                    rowEditor={allowEdit}
                    header="Edit"
                    style={{ width: '8rem', textAlign: 'center' }}
                />
                )}
                {showEditDeleteColumn && (
                    <Column
                    rowEditor
                    header={('Actions')}
                    body={(rowData, options) => (
                      <>
                        {options.rowEditor?.editing ? (
                          <>
                            <Button
                              icon="pi pi-save"
                              className="p-button-rounded mr-2 p-button-outlined"
                              onClick={(e) =>
                                options.rowEditor?.onSaveClick &&
                                options.rowEditor?.onSaveClick(e)
                              }
                              tooltip={("Save")}
                              tooltipOptions={{ position: "top" }}
                            />
                            <Button
                              icon="pi pi-times"
                              className="p-button-rounded p-button-outlined"
                              onClick={(e) =>
                                options.rowEditor?.onCancelClick &&
                                options.rowEditor?.onCancelClick(e)
                              }
                              tooltip={("Cancel")}
                              tooltipOptions={{ position: "top" }}
                              severity="warning"
                            />
                          </>
                        ) : (
                          <>
                            <Button
                              icon="pi pi-pencil"
                              className="p-button-rounded mr-2 p-button-outlined"
                              onClick={(e) =>
                                options.rowEditor?.onInitClick &&
                                options.rowEditor?.onInitClick(e)
                              }
                              tooltip={("Edit")}
                              tooltipOptions={{ position: "top" }}
                              severity="success"
                            />
                            <Button
                              icon="pi pi-trash"
                              className="p-button-rounded p-button-outlined"
                              tooltip={("Delete Record")}
                              tooltipOptions={{ position: "top" }}
                              severity="danger"
                              onClick={() =>  handleDelete(rowData)}
                              />
                          </>
                              )}
                     </>
                    )}
                    />
                )}
                {userManager && (
                    <Column
                    rowEditor
                    header={('Actions')}
                    body={(rowData, options) => (
                      <>
                        {options.rowEditor?.editing ? (
                          <>
                            <Button
                              icon="pi pi-save"
                              className="p-button-rounded mr-2 p-button-outlined"
                              onClick={(e) =>
                                options.rowEditor?.onSaveClick &&
                                options.rowEditor?.onSaveClick(e)
                              }
                              tooltip={("Save")}
                              tooltipOptions={{ position: "top" }}
                            />
                            <Button
                              icon="pi pi-times"
                              className="p-button-rounded p-button-outlined"
                              onClick={(e) =>
                                options.rowEditor?.onCancelClick &&
                                options.rowEditor?.onCancelClick(e)
                              }
                              tooltip={("Cancel")}
                              tooltipOptions={{ position: "top" }}
                              severity="warning"
                            />
                          </>
                        ) : (
                          <>
                            <Button
                              icon="pi pi-pencil"
                              className="p-button-rounded mr-2 p-button-outlined"
                              onClick={(e) =>
                                options.rowEditor?.onInitClick &&
                                options.rowEditor?.onInitClick(e)
                              }
                              tooltip={("Edit")}
                              tooltipOptions={{ position: "top" }}
                              severity="success"
                            />
                            <Button
                              icon="pi pi-unlock"
                              className="p-button-rounded p-button-outlined"
                              tooltip={("Reset Password")}
                              tooltipOptions={{ position: "top" }}
                              severity="help"
                              onClick={() =>  openDialog(rowData)}
                              />
                  
                              {/* Dialog */}
                              <Dialog
                                  header={`Reset Password for ${selectedRow?.username}`}
                                  visible={isDialogVisible}
                                  style={{ width: "30vw" }}
                                  onHide={() => setDialogVisible(false)}
                              >
                                  <div className="flex flex-column gap-3">
                                      <label htmlFor="newPassword">Enter New Password:</label>
                                      <InputText
                                          id="new-Password"
                                          type="password"
                                          value={newPassword}
                                          autoComplete="new-password"
                                          onChange={(e) => setNewPassword(e.target.value)}
                                      />
                                  </div>
                                  <div className="flex justify-content-end gap-2 mt-4">
                                      <Button
                                          label="Cancel"
                                          icon="pi pi-times"
                                          className="p-button-text"
                                          onClick={() => setDialogVisible(false)}
                                      />
                                      <Button
                                          label="Save"
                                          icon="pi pi-check"
                                          className="p-button-primary"
                                          onClick={handleSavePassword}
                                      />
                                  </div>
                              </Dialog>
                          </>
                        )}
                      </>
                    )}
                  ></Column>
                )}
            </DataTable>
            </div>
        </div>
    );
};

export default GlobalTable;